import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CertificateDetailsModal = ({ isOpen, onClose, certificate }) => {
  if (!isOpen || !certificate) return null;

  const getStatusColor = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0 || daysUntilExpiry <= 14) return 'var(--color-error)';
    if (daysUntilExpiry <= 30) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getStatusLabel = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0) return 'Expired';
    if (daysUntilExpiry <= 14) return 'Critical';
    if (daysUntilExpiry <= 30) return 'Expiring Soon';
    return 'Valid';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-3xl" onClick={(e) => e?.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-foreground font-heading">Certificate Details</h2>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
            aria-label="Close"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground font-caption mb-2 block">Property ID</label>
              <p className="text-foreground font-data">{certificate?.propertyId}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground font-caption mb-2 block">Certificate Type</label>
              <p className="text-foreground">{certificate?.typeName}</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground font-caption mb-2 block">Property Address</label>
            <p className="text-foreground">{certificate?.address}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground font-caption mb-2 block">Issue Date</label>
              <p className="text-foreground font-data">{certificate?.issueDate}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground font-caption mb-2 block">Expiry Date</label>
              <p className="text-foreground font-data">{certificate?.expiryDate}</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground font-caption mb-2 block">Status</label>
            <div className="flex items-center gap-3">
              <div
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: getStatusColor(certificate?.daysUntilExpiry) }}
              />
              <span
                className="text-lg font-semibold"
                style={{ color: getStatusColor(certificate?.daysUntilExpiry) }}
              >
                {getStatusLabel(certificate?.daysUntilExpiry)}
              </span>
              <span className="text-sm text-muted-foreground">
                ({certificate?.daysUntilExpiry < 0 ? 'Overdue' : `${certificate?.daysUntilExpiry} days remaining`})
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground font-caption mb-2 block">Issued By</label>
              <p className="text-foreground">{certificate?.contractor}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground font-caption mb-2 block">Certificate Number</label>
              <p className="text-foreground font-data">{certificate?.certificateNumber}</p>
            </div>
          </div>

          {certificate?.hasDocument && (
            <div className="p-4 rounded-lg bg-input border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Icon name="FileText" size={24} color="var(--color-accent)" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{certificate?.typeName}.pdf</p>
                    <p className="text-xs text-muted-foreground">2.4 MB • Uploaded {certificate?.issueDate}</p>
                  </div>
                </div>
                <Button variant="outline" iconName="Download" iconPosition="left">
                  Download
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-6 border-t border-border">
            <Button variant="outline" fullWidth onClick={onClose}>
              Close
            </Button>
            <Button variant="default" fullWidth iconName="Mail" iconPosition="left">
              Generate Reminder
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateDetailsModal;